import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, CloudOff, RotateCw, Search, ClipboardList, ChevronRight } from "lucide-react";
import { ListRow, NumberTile, SecondaryAction } from "./kit";
import { getWithRetry } from "../lib/fetchWithRetry";
import { clientDateHeaders, todayIso } from "../lib/trainingStatus";
import { newUi } from "../lib/featureFlags";
import { listMyGames } from "../lib/coachGames";
import { planAddOptions } from "../lib/activityCreate";
import { buildComingUp, whenLabel, overdueTrainingCount, TRAINING_LIST_PATH, COMING_UP_HORIZON_DAYS } from "../lib/comingUp";
import { overdueMatches } from "../lib/plannedMatches";
import PlannedMatchOverdueCard from "./PlannedMatchOverdueCard";
import AddedMatchReflection from "./AddedMatchReflection";

/**
 * Issue #1149 (D214, D225 item 3) — Coming up on Plan: the counts (This week,
 * Matches, Training, Goals), the rows soonest first, and one `Add` menu
 * offering Match, Training and Goals. Drawn in `PlanComingUp.dc.html` (round 10).
 *
 * Rows come from `GET /activities/upcoming` (matches and training, #1148) and
 * the goals `/development` has already loaded: a goal is a row only when it
 * has a target date. The Add menu's items are `planAddOptions(forRole)`, the
 * `activityCreateOptions` table read through its Plan face, never a second
 * list. Match and Training navigate (Plan a match is `/development?plan=match`,
 * Training is the plan-ahead dialog); Goals is `onAddGoal`, because the new-goal
 * form is a state of `/development` itself and a `?new=1` set on a mounted page
 * would only be stripped again by `useCreateDialog`.
 *
 * D57: a failed load is said on the page, in place, with Try again; it is never
 * the empty card, because "nothing coming up" would be a claim about data the
 * app never received (#271). `emptyCard={false}` is for the create-a-plan
 * screen: a brand-new player keeps the Add control but is not shown an empty
 * card above the one sentence they came to write.
 */
export default function PlanComingUp({ goals = [], forRole = "player", onAddGoal, emptyCard = true, added = false }) {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // "loading" | "ok" | "failed"
  const [items, setItems] = useState([]);
  const [overdue, setOverdue] = useState(0);
  const [games, setGames] = useState([]);
  // Issue #1150: the player's planned matches, from which the ones past their
  // date and time become the PlanMatchAfter card. `/activities/upcoming` lists
  // only today onward, so a match from last week is not in `items`.
  const [plannedMatches, setPlannedMatches] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // #1397 (D360): "Add a reflection" on the pill. The link shows until the form is closed
  // (saved or Not now); that is kept with the match in the page (`added.reflected`), so a
  // remount of this component never offers it again over a saved reflection.
  const [reflectOpen, setReflectOpen] = useState(false);
  const [pillFocused, setPillFocused] = useState(false);
  const pillRef = useRef(null);

  // Issue #1319 (D302): a player on the Pick XI flag also sees their coach's
  // games here. Read once per mount, so the flag is not a dependency.
  const coachGamesOn = forRole === "player" && newUi("pick-xi");

  const load = useCallback(() => {
    setState("loading");
    // One failed read is a failed Coming up: "nothing coming up" would be a
    // claim about data the app never received, so either failing shows the
    // failed state and one Try again reloads both.
    return Promise.all([
      getWithRetry(`/activities/upcoming?days=${COMING_UP_HORIZON_DAYS}&surface=plan`, { headers: clientDateHeaders() }),
      coachGamesOn ? listMyGames() : Promise.resolve([]),
      // A coach has no planned matches of their own to finish (#1150).
      // An optional prompt: if this read fails Coming up still shows, without the card.
      forRole === "player"
        ? getWithRetry("/matches?status=planned").catch((e) => {
            console.error("PlanComingUp: failed to load planned matches", e);
            return null;
          })
        : Promise.resolve(null),
    ])
      .then(([r, coachGames, planned]) => {
        setItems(r?.data?.items || []);
        setPlannedMatches(Array.isArray(planned?.data) ? planned.data : []);
        setOverdue(overdueTrainingCount(r?.data));
        setGames(coachGames);
        setState("ok");
      })
      .catch((e) => {
        console.error("PlanComingUp: failed to load what's coming up", e);
        setState("failed");
      });
  }, [coachGamesOn, forRole]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [menuOpen]);

  const today = todayIso();
  // #1150: a planned match past its date and time is the card, not a row, so it
  // leaves `items` here and is neither listed nor counted as coming up.
  const overdueCards = overdueMatches(plannedMatches);
  const overdueIds = new Set(overdueCards.map((m) => String(m.match_id)));
  const { rows, counts } = buildComingUp({
    items: items.filter((i) => !(i.kind === "match" && overdueIds.has(String(i.id)))),
    goals,
    today,
    games,
  });

  const choose = (option) => {
    setMenuOpen(false);
    if (option.key === "goal" && onAddGoal) onAddGoal();
    else navigate(option.to);
  };

  return (
    <section data-testid="plan-coming-up" className="flex flex-col" style={{ gap: 12 }}>
      <div className="flex items-end justify-between" style={{ gap: 12 }}>
        <div className="flex flex-col" style={{ gap: 4, minWidth: 0 }}>
          <div className="nw-eyebrow">Upcoming</div>
          <div className="nw-title">Coming up</div>
        </div>
        <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            type="button"
            data-testid="plan-add"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center justify-center"
            style={{
              minHeight: "var(--nw-size-tap)",
              minWidth: "var(--nw-size-tap)",
              gap: 6,
              padding: "0 16px",
              background: "var(--nw-surface)",
              border: "1px solid var(--nw-navy)",
              borderRadius: "var(--nw-radius-control)",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontWeight: 600,
              fontSize: 15,
              color: "var(--nw-navy)",
              cursor: "pointer",
            }}
          >
            <Plus size={16} aria-hidden="true" />
            Add
          </button>
          {menuOpen && (
            <div
              role="menu"
              data-testid="plan-add-menu"
              style={{
                position: "absolute",
                right: 0,
                top: "calc(100% + 6px)",
                zIndex: 20,
                width: 260,
                maxWidth: "calc(100vw - 40px)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line)",
                borderRadius: "var(--nw-radius-card)",
                overflow: "hidden",
              }}
            >
              {planAddOptions(forRole).map((o, i) => (
                <button
                  key={o.key}
                  type="button"
                  role="menuitem"
                  data-testid={`plan-add-${o.key}`}
                  onClick={() => choose(o)}
                  className="flex flex-col"
                  style={{
                    width: "100%",
                    minHeight: 52,
                    gap: 1,
                    padding: "8px 14px",
                    textAlign: "left",
                    background: "var(--nw-surface)",
                    border: "none",
                    borderTop: i === 0 ? "none" : "1px solid var(--nw-line)",
                    cursor: "pointer",
                  }}
                >
                  <span className="nw-rowname">{o.label}</span>
                  <span className="nw-help">{o.hint}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {state === "failed" ? (
        <div
          data-testid="coming-up-error"
          className="flex flex-col"
          style={{
            gap: 8,
            padding: 16,
            background: "var(--nw-surface)",
            border: "1px solid var(--nw-line)",
            borderLeft: "3px solid var(--nw-alert)",
            borderRadius: "var(--nw-radius-card)",
          }}
        >
          <div className="flex items-center" style={{ gap: 8 }}>
            <CloudOff size={18} aria-hidden="true" style={{ color: "var(--nw-ink-muted)" }} />
            <span className="nw-rowname">Couldn't load what's coming up</span>
          </div>
          <div className="nw-help">Nothing has been lost. We just couldn't reach the server.</div>
          <SecondaryAction data-testid="coming-up-retry" onClick={load} style={{ gap: 8 }}>
            <RotateCw size={16} aria-hidden="true" />
            Try again
          </SecondaryAction>
        </div>
      ) : state === "loading" ? (
        <div className="nw-help" data-testid="coming-up-loading">
          Loading what's coming up…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4" style={{ gap: 8 }} data-testid="coming-up-counts">
            <NumberTile variant="tile" label="This week" value={counts.thisWeek} data-testid="coming-up-count-week" />
            <NumberTile variant="tile" label="Matches" value={counts.matches} data-testid="coming-up-count-matches" />
            <NumberTile variant="tile" label="Training" value={counts.training} data-testid="coming-up-count-training" />
            <NumberTile variant="tile" label="Goals" value={counts.goals} data-testid="coming-up-count-goals" />
          </div>
          {/* The overdue match cards (#1150) are the first children of coming-up-rows; this slot stays the training prompt's. */}
          {overdue > 0 && (
            <Link
              to={TRAINING_LIST_PATH}
              data-testid="dash-needs-reflection"
              className="flex items-center"
              style={{
                gap: 10,
                padding: 12,
                minHeight: "var(--nw-size-tap)",
                background: "var(--nw-surface)",
                border: "1px solid var(--nw-line-strong)",
                borderLeft: "3px solid var(--nw-navy)",
                borderRadius: 10,
                fontSize: 13,
                color: "var(--nw-ink)",
                textDecoration: "none",
              }}
            >
              <ClipboardList size={16} aria-hidden="true" style={{ color: "var(--nw-navy)", flexShrink: 0 }} />
              <span>
                <b data-testid="dash-needs-reflection-count">{overdue}</b>{" "}
                {overdue === 1
                  ? "training session has been and gone \u2014 add how it went."
                  : "training sessions have been and gone \u2014 add how they went."}
              </span>
              <ChevronRight size={14} aria-hidden="true" style={{ color: "var(--nw-navy)", marginLeft: "auto", flexShrink: 0 }} />
            </Link>
          )}
          {rows.length > 0 || overdueCards.length > 0 ? (
            <div className="flex flex-col" style={{ gap: 8 }} data-testid="coming-up-rows">
              {overdueCards.map((m) => (
                <PlannedMatchOverdueCard key={m.match_id} match={m} testId={`plan-match-overdue-${m.match_id}`} from="/development" />
              ))}
              {rows.map((r) => (
                <ListRow
                  key={r.key}
                  data-testid={`coming-up-row-${r.key}`}
                  name={r.title}
                  figuresLine={[whenLabel(r.date, r.time, today), r.place].filter(Boolean).join(" · ")}
                  tag={r.tag}
                  chip={r.chip}
                  chevron={!!r.to}
                  onClick={r.to ? () => navigate(r.to) : undefined}
                />
              ))}
            </div>
          ) : (
            emptyCard && (
              <div
                data-testid="coming-up-empty"
                className="flex flex-col items-center text-center"
                style={{
                  gap: 6,
                  padding: 20,
                  background: "var(--nw-surface)",
                  border: "1px solid var(--nw-line)",
                  borderRadius: "var(--nw-radius-card)",
                }}
              >
                <Search size={22} aria-hidden="true" style={{ color: "var(--nw-ink-muted)" }} />
                <div className="nw-rowname">Nothing coming up.</div>
                <div className="nw-help">Add a match, a training session or a goal and it shows here, soonest first.</div>
              </div>
            )
          )}
          {/* The board's "Saved" frame (round-10 PlanMatchAfter): one sentence in an ink pill,
              centred, after the Coming up list. No glyph (D349). #1397 (D360, Martin): when the
              finished match is known, a small "Add a reflection" link sits on the pill, in the
              surface colour: the drawn navy is 1.3:1 on ink, and 4.5:1 is the rule. */}
          {added && (
            <div
              ref={pillRef}
              tabIndex={-1}
              onFocus={() => setPillFocused(true)}
              onBlur={() => setPillFocused(false)}
              role="status"
              data-testid="plan-match-added"
              style={{ alignSelf: "center", display: "flex", flexDirection: "column", alignItems: "center", padding: "10px 14px", background: "var(--nw-ink)", color: "var(--nw-surface)", borderRadius: 10, fontSize: 13, fontWeight: 600, outline: pillFocused ? "2px solid var(--nw-navy)" : "none", outlineOffset: 2 }}
            >
              <span data-testid="plan-match-added-text">Added to your record</span>
              {added.id && !added.reflected && !reflectOpen && (
                <SecondaryAction
                  variant="link"
                  data-testid="plan-match-added-reflect-link"
                  onClick={() => setReflectOpen(true)}
                  style={{ color: "var(--nw-surface)", fontSize: 13, textDecoration: "underline" }}
                >
                  Add a reflection
                </SecondaryAction>
              )}
            </div>
          )}
          {added?.id && !added.reflected && reflectOpen && (
            <AddedMatchReflection
              match={added}
              onDone={() => {
                setReflectOpen(false);
                added.markReflected?.();
                requestAnimationFrame(() => pillRef.current?.focus());
              }}
            />
          )}
        </>
      )}
    </section>
  );
}
